// S4 (v0.15.0) — Role list (system + custom).
import { useState } from "react";
import { Link } from "react-router-dom";
import { PlusIcon, EditIcon, EyeIcon, Trash2Icon } from "lucide-react";

import { useDeleteRole, useRoles, type Role } from "@/api/s4";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

// A34 — wire the existing useDeleteRole hook to a Delete button. Custom roles only
// (system roles are undeletable); disabled while the role still has assigned users.
function RoleRowActions({ role }: { role: Role }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const del = useDeleteRole();
  const inUse = role.user_count > 0;
  return (
    <div className="flex items-center justify-end gap-1">
      <Button size="sm" variant="ghost" asChild>
        <Link to={`/roles/${role.id}/edit`}>
          {role.is_system_role ? (
            <EyeIcon className="h-3 w-3" />
          ) : (
            <EditIcon className="h-3 w-3" />
          )}
        </Link>
      </Button>
      {!role.is_system_role && (
        <Button
          size="sm"
          variant="ghost"
          className="text-red-600 hover:text-red-700"
          disabled={inUse}
          title={inUse ? "Reassign its users before deleting" : "Delete role"}
          onClick={() => setConfirmOpen(true)}
        >
          <Trash2Icon className="h-3 w-3" />
        </Button>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete role?"
        description={
          <>
            Delete the <span className="font-semibold">{role.name}</span> role. This
            cannot be undone.
          </>
        }
        confirmLabel="Delete role"
        busy={del.isPending}
        onConfirm={() =>
          del.mutate(role.id, { onSuccess: () => setConfirmOpen(false) })
        }
      />
    </div>
  );
}

export default function RoleListPage() {
  const { data: roles = [], isLoading, isError, refetch } = useRoles();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Roles</h1>
          <p className="text-sm text-muted-foreground">
            System roles ship with the platform. Custom roles are per-tenant.
          </p>
        </div>
        <Button asChild className="bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90">
          <Link to="/roles/create">
            <PlusIcon className="mr-1 h-4 w-4" />
            Create Custom Role
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-right">Permissions</th>
                <th className="px-3 py-2 text-right">Users</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isError ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center">
                    {/* ERROR IS NOT EMPTY. An empty table reads as a fact about
                        the fleet; a failed fetch must say so and offer a retry. */}
                    <span className="text-destructive">Could not load roles.</span>{" "}
                    <button
                      type="button"
                      onClick={() => refetch()}
                      className="underline underline-offset-2"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    No roles yet.
                  </td>
                </tr>
              ) : (
                roles.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2">
                      <Badge variant={r.is_system_role ? "online" : "offline"}>
                        {r.is_system_role ? "System" : "Custom"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.permissions.length}
                    </td>
                    <td className="px-3 py-2 text-right">{r.user_count}</td>
                    <td className="px-3 py-2 text-right">
                      <RoleRowActions role={r} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
